import React, { useState, useEffect } from 'react';
import { FaTruck, FaTools, FaSync, FaCheck, FaWrench, FaBan } from 'react-icons/fa';
import axiosInstance from '../../../config/axios';
import { personalakteStyles as ps, colors } from '../../employees/styles/personalakteStyles';

interface Asset {
  id: number;
  name: string;
  code?: string;
  asset_type?: string;
  category?: string;
  short_description?: string;
  serial_number?: string;
  year?: number;
  license_plate?: string;
  status?: string;
  is_active?: boolean;
}

const getAssetIcon = (assetType?: string) => {
  switch (assetType?.toUpperCase()) {
    case 'VEHICLE':
    case 'TRAILER':
      return <FaTruck size={13} color={colors.slate} />;
    case 'TOOL':
      return <FaTools size={13} color={colors.warning} />;
    default:
      return <FaWrench size={13} color={colors.textMuted} />;
  }
};

const getStatusBadge = (status?: string) => {
  switch (status?.toUpperCase()) {
    case 'ACTIVE':
      return { bg: colors.successLight, color: '#065f46', icon: <FaCheck size={9} />, label: 'Aktiv' };
    case 'MAINTENANCE':
      return { bg: colors.warningLight, color: '#92400e', icon: <FaWrench size={9} />, label: 'Wartung' };
    case 'OUT_OF_SERVICE':
      return { bg: colors.dangerLight, color: '#991b1b', icon: <FaBan size={9} />, label: 'Außer Betrieb' };
    case 'RETIRED':
      return { bg: colors.bgMuted, color: colors.textBody, icon: <FaBan size={9} />, label: 'Ausgemustert' };
    default:
      return { bg: colors.bgMuted, color: colors.textBody, icon: null, label: status || '—' };
  }
};

export default function AssetsTab(): React.JSX.Element {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const parseList = <T,>(data: any, keys: string[]): T[] => {
    if (Array.isArray(data)) return data;
    for (const k of keys) {
      if (Array.isArray(data?.[k])) return data[k];
    }
    return [];
  };

  const loadAssets = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get<any>('/api/admin/assets/');
      const data = parseList<Asset>(response.data, ['assets', 'results']);
      setAssets(data);
    } catch (err: any) {
      console.error('Failed to load assets:', err);
      setError(err?.response?.status === 404
        ? 'Assets-Endpoint nicht verfügbar (/api/admin/assets/)'
        : 'Fehler beim Laden der Assets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssets();
  }, []);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 40, color: colors.textMuted }}>Lade Assets...</div>;
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <div style={{ color: colors.danger, marginBottom: 16, fontSize: 13 }}>{error}</div>
        <button onClick={loadAssets} className="hover-btn-secondary" style={ps.buttonSecondary}>
          <FaSync size={11} />
          Erneut versuchen
        </button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: colors.navy, margin: 0 }}>
          Assets ({assets.length})
        </h3>
        <button onClick={loadAssets} className="hover-btn-secondary" style={ps.buttonSecondary}>
          <FaSync size={10} />
          Aktualisieren
        </button>
      </div>

      {assets.length === 0 ? (
        <div style={{ ...ps.innerContainer, textAlign: 'center', padding: '60px 20px' }}>
          <FaTools size={28} color={colors.textLight} style={{ marginBottom: 16 }} />
          <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 8, color: colors.textMuted }}>
            Keine Assets vorhanden
          </div>
          <div style={{ fontSize: 12, color: colors.textLight }}>
            Führe <code style={{ background: colors.bgMuted, padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>
              python manage.py seed_assets --tenant=ID
            </code> aus, um Test-Assets anzulegen.
          </div>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={thStyle}>ID</th>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Code</th>
                <th style={thStyle}>Typ</th>
                <th style={thStyle}>Kategorie</th>
                <th style={thStyle}>Kennzeichen</th>
                <th style={thStyle}>Seriennr.</th>
                <th style={thStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {assets.map((asset) => {
                const statusBadge = getStatusBadge(asset.status);
                return (
                  <tr key={asset.id} className="hover-list-item" style={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                    <td style={tdStyle}>{asset.id}</td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {getAssetIcon(asset.asset_type)}
                        <span style={{ fontWeight: 600 }}>{asset.name}</span>
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <code style={{ background: colors.bgMuted, padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>
                        {asset.code || '—'}
                      </code>
                    </td>
                    <td style={tdStyle}>{asset.asset_type || '—'}</td>
                    <td style={tdStyle}>{asset.category || '—'}</td>
                    <td style={tdStyle}>{asset.license_plate || '—'}</td>
                    <td style={tdStyle}>{asset.serial_number || '—'}</td>
                    <td style={tdStyle}>
                      <span style={ps.badge(statusBadge.bg, statusBadge.color)}>
                        {statusBadge.icon}
                        {statusBadge.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 16px',
  fontWeight: 600,
  color: colors.textLight,
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  borderBottom: `1px solid ${colors.border}`,
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  color: colors.textBody,
  fontSize: 13,
};
